import { formatUtcDate } from './formatDate';
import type { CompanySubscriptionResponse } from './types';

export type NoticeTone = 'info' | 'warning' | 'danger';

export interface NoticeAction {
  label: string;
  to: string;
  primary: boolean;
}

export type NoticeId =
  | 'no-plan' | 'trial-not-started' | 'trial-active' | 'trial-ending' | 'trial-expired'
  | 'past-due-grace' | 'past-due-restricted' | 'canceled' | 'cancel-scheduled';

export interface StateNotice {
  id: NoticeId;
  tone: NoticeTone;
  title: string;
  message: string;
  actions: NoticeAction[];
  /** True when the server says the company is read-only (`accessMode` ReadOnly). Mirrors the server; never derived. */
  readOnly: boolean;
}

const PLANS_ROUTE = '/settings/subscription';
const BILLING_ROUTE = '/settings/billing';

function daysPhrase(days: number): string {
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * The ONE place a subscription's server-decided fields become the banner/notice copy. It reads only
 * `effectiveState`, `accessMode`, `requiredAction`, `trialEndingThresholdDays`, `trialDaysRemaining`,
 * `graceEndsUtc`, `cancelAtPeriodEnd`, `currentPeriodEndUtc` and the plan name — every one supplied by the API.
 * It never compares a date with the browser clock: an expired trial, a grace period that has run out and a
 * canceled subscription are all different `effectiveState` values the server already decided.
 *
 * `null` means "no subscription row" (`NoSubscription`). Returns null when no notice is warranted (a paid,
 * healthy subscription).
 */
export function describeSubscriptionState(subscription: CompanySubscriptionResponse | null): StateNotice | null {
  if (!subscription) {
    return {
      id: 'no-plan',
      tone: 'info',
      title: 'Choose a plan to get started',
      message: 'Start a free 30-day trial (no credit card needed) or subscribe. You can set up your company and employees now; payroll processing unlocks once you start a trial or subscribe.',
      actions: [
        { label: 'Start free trial', to: PLANS_ROUTE, primary: true },
        { label: 'Compare plans', to: PLANS_ROUTE, primary: false },
      ],
      readOnly: false,
    };
  }

  const readOnly = subscription.accessMode === 'ReadOnly';

  switch (subscription.effectiveState) {
    case 'NoSubscription':
      return describeSubscriptionState(null);

    case 'TrialNotStarted':
      return {
        id: 'trial-not-started',
        tone: 'info',
        title: 'Your free trial has not started',
        message: `${subscription.planName} is selected. Start your ${subscription.trialLengthDays}-day free trial whenever you are ready. No credit card is needed, and we will never charge you automatically.`,
        actions: [
          { label: 'Start free trial', to: PLANS_ROUTE, primary: true },
          { label: 'Compare plans', to: PLANS_ROUTE, primary: false },
        ],
        readOnly,
      };

    case 'TrialActive': {
      const left = subscription.trialDaysRemaining;
      const ends = formatUtcDate(subscription.trialEndUtc);
      if (subscription.trialEndingThresholdDays !== null) {
        const days = left ?? subscription.trialEndingThresholdDays;
        return {
          id: 'trial-ending',
          tone: 'warning',
          title: `Your free trial ends in ${daysPhrase(days)}`,
          message: `Your trial ends on ${ends}. Subscribe before then to keep your plan without interruption. After the trial the company becomes read-only until you subscribe, and your records are kept. We will never charge you automatically.`,
          actions: [
            { label: 'Upgrade now', to: PLANS_ROUTE, primary: true },
            { label: 'Compare plans', to: PLANS_ROUTE, primary: false },
          ],
          readOnly,
        };
      }
      return {
        id: 'trial-active',
        tone: 'info',
        title: left !== null ? `You are on a free trial: ${daysPhrase(left)} left` : 'You are on a free trial',
        message: `Your ${subscription.planName} trial ends on ${ends}. No card is on file and we will never charge you automatically. Subscribe any time to keep your plan after the trial.`,
        actions: [
          { label: 'Upgrade now', to: PLANS_ROUTE, primary: true },
          { label: 'Compare plans', to: PLANS_ROUTE, primary: false },
        ],
        readOnly,
      };
    }

    case 'TrialExpired':
      return {
        id: 'trial-expired',
        tone: 'danger',
        title: 'Your free trial has ended',
        message: 'The company is now read-only: you can view your records, but changes are paused. Subscribe to continue. Your records are kept.',
        actions: [
          { label: 'Subscribe', to: PLANS_ROUTE, primary: true },
          { label: 'Compare plans', to: PLANS_ROUTE, primary: false },
        ],
        readOnly,
      };

    case 'PastDueGrace': {
      const until = subscription.graceEndsUtc ? ` Your plan access continues until ${formatUtcDate(subscription.graceEndsUtc)}.` : ' Your plan access continues for now.';
      return {
        id: 'past-due-grace',
        tone: 'warning',
        title: 'Your last payment did not go through',
        message: `Please update your payment method.${until} We will retry the payment automatically.`,
        actions: [
          { label: 'Update payment method', to: BILLING_ROUTE, primary: true },
          { label: 'View billing', to: BILLING_ROUTE, primary: false },
        ],
        readOnly,
      };
    }

    case 'PastDueRestricted':
      return {
        id: 'past-due-restricted',
        tone: 'danger',
        title: 'Payment failed: the company is read-only',
        message: 'Update your payment method to restore full access. It stays read-only until the payment is collected. Your records are safe, and billing stays available.',
        actions: [
          { label: 'Update payment method', to: BILLING_ROUTE, primary: true },
          { label: 'View billing', to: BILLING_ROUTE, primary: false },
        ],
        readOnly,
      };

    case 'Canceled':
      return {
        id: 'canceled',
        tone: 'danger',
        title: 'Your subscription has been canceled',
        message: 'The company is read-only. Reactivate your subscription to make changes again. Your records are kept.',
        actions: [
          { label: 'Reactivate subscription', to: BILLING_ROUTE, primary: true },
        ],
        readOnly,
      };

    case 'Active':
    case 'PendingChange':
      if (subscription.cancelAtPeriodEnd) {
        return {
          id: 'cancel-scheduled',
          tone: 'info',
          title: 'Your subscription is set to cancel',
          message: `It stays fully active until ${formatUtcDate(subscription.currentPeriodEndUtc)}. Cancellation takes effect at the end of the current billing period.`,
          actions: [{ label: 'Keep my subscription', to: BILLING_ROUTE, primary: true }],
          readOnly,
        };
      }
      return null;

    default:
      return null;
  }
}
